import * as Yup from 'yup'
import { ChatbotType } from '@/redux/types/configuration'
import { LLMChoice } from '@/redux/types/room'

export const initialValues = {
  greetings: '',
  prompt: '',
  scenario: '',
  restrictions: '',
  temperature: 0.7,
  max_tokens: 1024,
  history_limit: 25,
  chatbot_llm: LLMChoice.CLAUDE,
  rag_enabled: true,
  rag_top_k: 5,
  rag_chunk_size: 1000,
  rag_chunk_overlap: 200,
  rag_similarity_threshold: 0.2,
  rag_max_context_length: 3000,
  currency_cost: 0,
}

const isRichTextEmpty = (value: string): boolean => {
  if (!value) return true

  const textContent = value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim()

  return textContent.length === 0
}

const createRichTextValidation = (fieldName: string) => {
  return Yup.string().test(
    'rich-text-required',
    `${fieldName} ${fieldName.endsWith('s') ? 'are' : 'is'} required`,
    (value) => {
      return !isRichTextEmpty(value || '')
    }
  )
}

// Default schema for regular chatbots
export const advisorSettingsSchema = Yup.object().shape({
  greetings: createRichTextValidation('Greetings'),
  prompt: createRichTextValidation('Prompt'),
  scenario: createRichTextValidation('Scenario'),
  restrictions: createRichTextValidation('Restrictions'),
})

export const randrSettingsSchema = Yup.object().shape({
  prompt: createRichTextValidation('Prompt'),
  greetings: createRichTextValidation('Greetings'),
  scenario: createRichTextValidation('Scenario'),
  restrictions: createRichTextValidation('Restrictions'),
})


export const getValidationSchema = (
  chatbotType: ChatbotType | null,
  useRoomDefaultBehavioralConfig?: boolean
) => {
  if (useRoomDefaultBehavioralConfig) {
    return Yup.object().shape({})
  }

  if (!chatbotType) return advisorSettingsSchema

  return chatbotType === ChatbotType.RANDR ||
    chatbotType === ChatbotType.ADJUDICATION
    ? randrSettingsSchema
    : advisorSettingsSchema
}

// Validation schema for document RAG settings
export const documentRAGSettingsSchema = Yup.object().shape({
  use_full_context: Yup.boolean().default(false),
  rag_enabled: Yup.boolean().nullable(),
  rag_top_k: Yup.number()
    .nullable()
    .min(1, 'Top K must be at least 1')
    .max(20, 'Top K must be at most 20')
    .when('use_full_context', {
      is: true,
      then: (schema) => schema.nullable(),
    }),
  rag_chunk_size: Yup.number()
    .nullable()
    .min(100, 'Chunk size must be at least 100')
    .max(4000, 'Chunk size must be at most 4000')
    .when('use_full_context', {
      is: true,
      then: (schema) => schema.nullable(),
    }),
  rag_chunk_overlap: Yup.number()
    .nullable()
    .min(0, 'Chunk overlap must be at least 0')
    .max(1000, 'Chunk overlap must be at most 1000')
    .when('use_full_context', {
      is: true,
      then: (schema) => schema.nullable(),
    }),
  rag_similarity_threshold: Yup.number()
    .nullable()
    .min(0.0, 'Similarity threshold must be at least 0.0')
    .max(1.0, 'Similarity threshold must be at most 1.0')
    .when('use_full_context', {
      is: true,
      then: (schema) => schema.nullable(),
    }),
  rag_max_context_length: Yup.number()
    .nullable()
    .min(500, 'Max context length must be at least 500')
    .max(8000, 'Max context length must be at most 8000')
    .when('use_full_context', {
      is: true,
      then: (schema) => schema.nullable(),
    }),
}).test(
  'full-context-no-rag',
  'Full context files cannot have RAG settings configured',
  function (value) {
    if (value?.use_full_context) {
      // If use_full_context is true, all RAG settings should be null
      const hasRAGSettings = 
        value.rag_enabled !== null ||
        value.rag_top_k !== null ||
        value.rag_chunk_size !== null ||
        value.rag_chunk_overlap !== null ||
        value.rag_similarity_threshold !== null ||
        value.rag_max_context_length !== null
      
      if (hasRAGSettings) {
        return this.createError({
          message: 'Full context files cannot have RAG settings configured',
        })
      }
    }
    return true
  }
)

export const roomDocumentRAGSettingsSchema = Yup.object()
  .shape({
    use_full_context: Yup.boolean().default(false),
    rag_enabled: Yup.boolean().nullable(),
    rag_chunk_size: Yup.number()
      .nullable()
      .min(100, 'Chunk size must be at least 100')
      .max(4000, 'Chunk size must be at most 4000')
      .when('use_full_context', {
        is: true,
        then: (schema) => schema.nullable(),
      }),
    rag_chunk_overlap: Yup.number()
      .nullable()
      .min(0, 'Chunk overlap must be at least 0')
      .max(1000, 'Chunk overlap must be at most 1000')
      .when('use_full_context', {
        is: true,
        then: (schema) => schema.nullable(),
      }),
  })
  .test(
    'full-context-no-rag',
    'Full context files cannot have RAG settings configured',
    function (value) {
      if (value?.use_full_context) {
        const hasRAGSettings =
          value.rag_enabled !== null ||
          value.rag_chunk_size !== null ||
          value.rag_chunk_overlap !== null
        if (hasRAGSettings) {
          return this.createError({
            message: 'Full context files cannot have RAG settings configured',
          })
        }
      }
      return true
    }
  )

export { isRichTextEmpty }
